import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AdminPlanCohortPurseMappingDetail,
  AdminPlanCohortPurseMappingRow,
  PlanCohortPurseMappingHistoryRow,
  adminPlanCohortPurseMappingApi,
  RollbackMappingPayload,
} from '../../../../api/adminPlanCohortPurseMappingApi';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  detail: AdminPlanCohortPurseMappingDetail | null;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

function initialsFor(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local[0]];
  return picks.join('').toUpperCase().slice(0, 2);
}

function changeTypeChip(t: PlanCohortPurseMappingHistoryRow['change_type']): {
  bg: string;
  color: string;
  label: string;
} {
  switch (t) {
    case 'create':
      return { bg: colors.successBg, color: colors.successText, label: 'create' };
    case 'update':
      return { bg: colors.infoBg, color: colors.infoText, label: 'update' };
    case 'delete':
      return { bg: colors.errorBg, color: colors.errorText, label: 'delete' };
    case 'publish':
      return { bg: colors.brandLight, color: colors.textPrimary, label: 'publish' };
    case 'rollback':
      return { bg: colors.warningBg, color: colors.warningText, label: 'rollback' };
    default:
      return { bg: colors.bgMuted, color: colors.textSecondary, label: String(t) };
  }
}

const DIFF_FIELDS: { key: keyof PlanCohortPurseMappingHistoryRow; label: string }[] = [
  { key: 'allocation_percent', label: 'allocation_percent' },
  { key: 'effective_date', label: 'effective_date' },
  { key: 'active', label: 'active' },
];

function renderVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') {
    const json = JSON.stringify(v);
    return json.length > 60 ? `${json.slice(0, 57)}…` : json;
  }
  return String(v);
}

interface RestoreDialogState {
  targetVersion: number | null;
}

const InlineRollback: React.FC<{
  open: boolean;
  onClose: () => void;
  mappingCode: string;
  targetVersion: number;
  currentVersion: number;
  onSuccess: (row: AdminPlanCohortPurseMappingRow) => void;
}> = ({ open, onClose, mappingCode, targetVersion, currentVersion, onSuccess }) => {
  const [conflict, setConflict] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: RollbackMappingPayload = {
        target_version: targetVersion,
        base_version: currentVersion,
      };
      return adminPlanCohortPurseMappingApi.rollback(mappingCode, payload);
    },
    onSuccess: (row) => {
      setConflict(null);
      onSuccess(row);
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && typeof data?.current_version === 'number') {
        setConflict(data.current_version);
      }
    },
  });

  const futureVersion = currentVersion + 1;

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-modal="true"
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 20,
        p: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: 420,
          width: '100%',
          backgroundColor: colors.bgWhite,
          borderRadius: '12px',
          p: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <Typography sx={{ ...typo.sectionTitle, fontSize: 16, mb: 1.5 }}>
          Restore{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 13 }}>
            {mappingCode}
          </Box>{' '}
          to v{targetVersion}?
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 1 }}>
          A new version <strong>v{futureVersion}</strong> will be created copying v
          {targetVersion}&apos;s data. Nothing is destroyed — history stays append-only.
        </Typography>
        <Typography
          sx={{ fontSize: 12, color: colors.textTertiary, mb: 2, fontStyle: 'italic' }}
        >
          Note: this does not affect tenants. platform_mapping_id and platform_version stay at
          their current values. Publish the restored version to propagate to tenants.
        </Typography>
        {conflict != null && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
            Someone else saved v{conflict}. Close and reload to pick up the latest.
          </Alert>
        )}
        {mutation.isError && conflict == null && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
            Restore failed — try again.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={secondaryButtonSx} disabled={mutation.isPending}>
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || conflict != null}
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
          >
            {mutation.isPending ? 'Restoring…' : 'Restore'}
          </WriteButton>
        </Box>
      </Box>
    </Box>
  );
};

interface NodeProps {
  row: PlanCohortPurseMappingHistoryRow;
  prev: PlanCohortPurseMappingHistoryRow | null;
  isCurrent: boolean;
  onRestore?: (version: number) => void;
}

const HistoryNode: React.FC<NodeProps> = ({ row, prev, isCurrent, onRestore }) => {
  const [diffOpen, setDiffOpen] = useState(false);
  const chip = changeTypeChip(row.change_type);

  return (
    <Box sx={{ position: 'relative', pl: 4, pb: 3 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 4,
          top: 4,
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: isCurrent ? colors.adminAccent : 'transparent',
          border: `2px solid ${colors.adminAccent}`,
          boxShadow: isCurrent ? `0 0 0 4px ${colors.adminAccentSubtle}` : undefined,
          zIndex: 2,
        }}
      />

      <Box
        sx={{
          border: `1px solid ${colors.borderLight}`,
          borderRadius: '10px',
          p: 2,
          backgroundColor: isCurrent ? colors.adminAccentSubtle : colors.bgWhite,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            component="span"
            sx={{
              ...typo.mono,
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              borderRadius: '4px',
              px: 0.75,
              py: 0.25,
            }}
          >
            v{row.version}
          </Box>
          <Chip
            size="small"
            label={chip.label}
            sx={{ fontSize: 11, fontWeight: 700, backgroundColor: chip.bg, color: chip.color }}
          />
          {isCurrent && (
            <Chip
              size="small"
              label="current"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Tooltip title={row.created_by || 'Unknown'}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: 10,
                fontWeight: 700,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
              }}
            >
              {initialsFor(row.created_by)}
            </Avatar>
          </Tooltip>
          <Typography sx={{ fontSize: 12, color: colors.textSecondary, flex: 1 }} noWrap>
            {row.created_by || '—'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: colors.textTertiary }}>
            {formatRelative(row.created_at)}
          </Typography>
        </Box>

        {row.change_summary && (
          <Typography
            sx={{
              fontSize: 12,
              color: colors.textSecondary,
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {row.change_summary}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!isCurrent && row.change_type !== 'delete' && onRestore && (
            <WriteButton
              size="small"
              onClick={() => onRestore(row.version)}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                minWidth: 0,
                p: 0.5,
                color: colors.adminAccent,
                fontWeight: 700,
              }}
            >
              Restore
            </WriteButton>
          )}
          <Button
            size="small"
            onClick={() => setDiffOpen((x) => !x)}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, p: 0.5 }}
          >
            {diffOpen ? 'Hide diff' : 'View diff'}
          </Button>
        </Box>

        {diffOpen && (
          <Box sx={{ mt: 1.5, borderTop: `1px dashed ${colors.borderLight}`, pt: 1 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.textTertiary }}>
                    Field
                  </TableCell>
                  <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.errorText }}>
                    Old
                  </TableCell>
                  <TableCell sx={{ border: 0, p: 0.5, fontSize: 11, color: colors.successText }}>
                    New
                  </TableCell>
                </TableRow>
                {DIFF_FIELDS.map(({ key, label }) => {
                  const newVal = row[key];
                  const oldVal = prev ? prev[key] : undefined;
                  const changed =
                    prev !== null &&
                    (typeof newVal === 'object' && typeof oldVal === 'object'
                      ? JSON.stringify(newVal) !== JSON.stringify(oldVal)
                      : newVal !== oldVal);
                  return (
                    <TableRow
                      key={String(key)}
                      sx={{ backgroundColor: changed ? colors.warningBg : 'transparent' }}
                    >
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: colors.textSecondary,
                        }}
                      >
                        {label}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: changed ? colors.errorText : colors.textTertiary,
                          backgroundColor: changed ? colors.errorBg : undefined,
                        }}
                      >
                        {renderVal(oldVal)}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: 0,
                          p: 0.5,
                          fontSize: 12,
                          fontFamily: 'Fira Code, monospace',
                          color: changed ? colors.successText : colors.textTertiary,
                          backgroundColor: changed ? colors.successBg : undefined,
                        }}
                      >
                        {renderVal(newVal)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const MappingVersionHistoryDrawer: React.FC<Props> = ({ open, onClose, detail }) => {
  const history = useMemo(() => detail?.history ?? [], [detail]);
  const queryClient = useQueryClient();

  const [rollback, setRollback] = useState<RestoreDialogState>({ targetVersion: null });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const handleRollbackSuccess = (row: AdminPlanCohortPurseMappingRow) => {
    const targetV = rollback.targetVersion;
    setRollback({ targetVersion: null });
    setSnackbar({
      open: true,
      msg: `Restored to v${targetV} as v${row.current_version}`,
      sev: 'success',
    });
    if (detail) {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'plan-cohort-purse', detail.mapping_code],
      });
    }
    queryClient.invalidateQueries({ queryKey: ['admin', 'plan-cohort-purse', 'list'] });
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, position: 'relative' } }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${colors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.bgSubtle,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>Version history</Typography>
          <Typography sx={{ ...typo.sectionTitle, fontSize: 16 }}>
            {detail?.mapping_code ?? '—'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ position: 'relative', p: 2.5, overflowY: 'auto' }}>
        {history.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: `calc(${20}px + 4px)`,
              top: 24,
              bottom: 24,
              width: 2,
              backgroundColor: colors.borderLight,
              zIndex: 1,
            }}
          />
        )}

        {history.length === 0 && (
          <Typography sx={{ ...typo.secondaryText, textAlign: 'center', mt: 4 }}>
            No history yet.
          </Typography>
        )}

        {history.map((row, idx) => {
          const prev = history[idx + 1] ?? null;
          const isCurrent = detail?.current_version === row.version;
          return (
            <HistoryNode
              key={`${row.version}-${row.created_at}`}
              row={row}
              prev={prev}
              isCurrent={isCurrent}
              onRestore={(v) => setRollback({ targetVersion: v })}
            />
          );
        })}
      </Box>

      {detail && rollback.targetVersion != null && (
        <InlineRollback
          open={rollback.targetVersion != null}
          onClose={() => setRollback({ targetVersion: null })}
          mappingCode={detail.mapping_code}
          targetVersion={rollback.targetVersion}
          currentVersion={detail.current_version}
          onSuccess={handleRollbackSuccess}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default MappingVersionHistoryDrawer;
